import { EtagMismatch, FetchSource, PMTiles, type RangeResponse, type Source } from "pmtiles";

// PMTiles reads the archive as byte ranges: a 16KB header, then a directory
// per region, then one range per tile. Those offsets are deterministic, so the
// same view always asks for the same ranges — which makes them worth keeping.
//
// MapLibre caches parsed tiles and PMTiles caches directories, but both die with
// the page, and browsers treat 206 responses as second-class cache citizens
// (PMTiles' own FetchSource even opts out of the HTTP cache entirely on
// Chromium/Windows). So ranges are stored in Cache Storage, which survives
// reloads: a second visit to a view already seen does no network at all.
const CACHE_NAME = "pmtiles-ranges-v1";
const ETAG_KEY = "__archive_etag__";

// z14 tiles in the planet archive run ~700KB, so these are deliberately
// generous. Cache Storage is evicted by the browser under disk pressure.
const MEMORY_MAX_BYTES = 64 * 1024 * 1024;
const PERSISTENT_MAX_ENTRIES = 800;
// Listing every key on each write would be wasteful; trim in batches.
const TRIM_EVERY = 25;

interface MemoryEntry {
  data: ArrayBuffer;
  etag?: string;
}

async function openCache(): Promise<Cache | null> {
  // Absent in insecure contexts and in some private-browsing modes; the source
  // still works, just without persistence.
  try {
    if (typeof caches === "undefined") return null;
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

export class CachedRangeSource implements Source {
  private readonly fetchSource: FetchSource;
  private readonly memory = new Map<string, MemoryEntry>();
  private memoryBytes = 0;
  private readonly cache: Promise<Cache | null>;
  private archiveEtag?: string;
  private writes = 0;

  constructor(private readonly url: string) {
    this.fetchSource = new FetchSource(url);
    this.cache = openCache();
    void this.revalidate();
  }

  getKey(): string {
    return this.url;
  }

  async getBytes(
    offset: number,
    length: number,
    signal?: AbortSignal,
    etag?: string,
  ): Promise<RangeResponse> {
    // The caller wants a different archive than the one we hold ranges for.
    // Purge before anything else, so the retry PMTiles does after an
    // EtagMismatch reaches the network instead of this cache again.
    if (etag && this.archiveEtag && etag !== this.archiveEtag) {
      await this.purge();
      throw new EtagMismatch(
        `Cached archive ETag ${this.archiveEtag} does not match ${etag}`,
      );
    }

    const key = `${offset}-${length}`;

    const hit = this.memory.get(key);
    if (hit) return { data: hit.data.slice(0), etag: hit.etag };

    const stored = await this.readPersistent(key);
    if (stored) {
      this.remember(key, stored);
      return { data: stored.data.slice(0), etag: stored.etag };
    }

    const res = await this.fetchSource.getBytes(offset, length, signal, etag);

    // A changed ETag invalidates everything already stored for this archive.
    if (res.etag && this.archiveEtag && res.etag !== this.archiveEtag) {
      await this.purge();
    }
    if (res.etag) this.archiveEtag = res.etag;

    const entry: MemoryEntry = { data: res.data, etag: res.etag };
    this.remember(key, entry);
    void this.writePersistent(key, entry);
    return { ...res, data: res.data.slice(0) };
  }

  private remember(key: string, entry: MemoryEntry) {
    this.memory.set(key, entry);
    this.memoryBytes += entry.data.byteLength;
    // Insertion-ordered, so the first key out is the oldest.
    while (this.memoryBytes > MEMORY_MAX_BYTES) {
      const oldest = this.memory.keys().next().value;
      if (oldest === undefined || oldest === key) break;
      const dropped = this.memory.get(oldest);
      this.memory.delete(oldest);
      if (dropped) this.memoryBytes -= dropped.data.byteLength;
    }
  }

  private request(key: string): Request {
    return new Request(`${this.url}?__pmtiles_range=${key}`);
  }

  private async readPersistent(key: string): Promise<MemoryEntry | null> {
    try {
      const cache = await this.cache;
      if (!cache) return null;
      const res = await cache.match(this.request(key));
      if (!res) return null;
      return {
        data: await res.arrayBuffer(),
        etag: res.headers.get("x-archive-etag") ?? undefined,
      };
    } catch {
      return null;
    }
  }

  private async writePersistent(key: string, entry: MemoryEntry) {
    try {
      const cache = await this.cache;
      if (!cache) return;
      const headers = new Headers();
      if (entry.etag) headers.set("x-archive-etag", entry.etag);
      await cache.put(this.request(key), new Response(entry.data, { headers }));
      if (++this.writes % TRIM_EVERY === 0) await this.trim(cache);
    } catch {
      // A full or unavailable cache must never fail a tile read.
    }
  }

  private async trim(cache: Cache) {
    const keys = await cache.keys();
    const excess = keys.length - PERSISTENT_MAX_ENTRIES;
    for (let i = 0; i < excess; i++) {
      const key = keys[i];
      if (key && !key.url.includes(ETAG_KEY)) await cache.delete(key);
    }
  }

  private async purge() {
    this.memory.clear();
    this.memoryBytes = 0;
    this.archiveEtag = undefined;
    try {
      // Emptied through the handle we hold rather than with caches.delete(),
      // which would leave this instance reading from a detached cache.
      const cache = await this.cache;
      if (!cache) return;
      for (const key of await cache.keys()) await cache.delete(key);
    } catch {
      // Nothing to do — a stale entry is caught again on the next read.
    }
  }

  // Served entirely from Cache Storage, a reload would never notice the archive
  // being replaced. One cheap range request off the critical path settles it:
  // tile reads don't wait on this, they just get purged out from under them if
  // the ETag moved.
  private async revalidate() {
    try {
      const cache = await this.cache;
      if (!cache) return;
      const marker = new Request(`${this.url}?__pmtiles_range=${ETAG_KEY}`);
      const known = (await cache.match(marker))?.headers.get("x-archive-etag");

      const res = await fetch(this.url, { headers: { range: "bytes=0-0" } });
      let live = res.headers.get("Etag");
      if (live?.startsWith("W/")) live = null;
      if (!live) return;

      if (known && known !== live) await this.purge();
      this.archiveEtag ??= live;

      const headers = new Headers({ "x-archive-etag": live });
      await (await this.cache)?.put(marker, new Response(null, { headers }));
    } catch {
      // Offline, or the probe was blocked — keep serving what we have.
    }
  }
}

/** A PMTiles archive whose byte ranges persist across page loads. */
export function cachedPMTiles(url: string): PMTiles {
  return new PMTiles(new CachedRangeSource(url));
}
